import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Button, Heading, Table, Thead, Tbody, Tr, Th, Td,
  Text, VStack, Spinner, useToast,
  FormControl, FormLabel, Select, Flex
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const ConfigurationAccuracyDashboard = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      if (!accessToken) return;
      try {
        const response = await callApi('/clients', accessToken);
        if (response.ok) {
          const data = await response.json();
          setClients(data.clients || []);
        }
      } catch (error) {
        console.error("Failed to fetch clients", error);
        toast({ title: "Error", description: "Failed to load clients", status: "error" });
      }
    };
    fetchClients();
  }, [accessToken, toast]);

  const fetchStats = async (clientId) => {
    if (!clientId) {
      setStats([]);
      return;
    }
    setLoading(true);
    try {
      const url = `/api/manual-coding/client-configurations-stats?client_id=${clientId}`;
      const response = await callApi(url, accessToken);
      const data = await response.json();
      if (response.ok) {
        setStats(Array.isArray(data) ? data : []);
      } else {
        throw new Error(data.error || 'Failed to fetch configuration stats');
      }
    } catch (error) {
      toast({ title: "Error", description: error.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    setSelectedClientId(clientId);
    fetchStats(clientId);
  };

  const handleRowClick = (configId) => {
    navigate('/cpt-feedback-dashboard', { 
        state: { 
          preselectedClientId: selectedClientId,
          preselectedConfigId: configId
        } 
    });
  };

  return (
    <Box>
        <HeaderMenus />
        <Box p={8}>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading>Configuration Accuracy Overview</Heading>
                <Button colorScheme="blue" onClick={() => navigate('/cpt-feedback-dashboard')}>
                    Back to Detailed Dashboard
                </Button>
            </Flex>

            <VStack mb={6} p={4} borderWidth="1px" borderRadius="lg" align="start">
              <Text>Select a client to see the accuracy of their configurations over time.</Text>
              <FormControl maxW="300px">
                <FormLabel htmlFor="client-select" mb="1">
                  Select Client
                </FormLabel>
                <Select
                  id="client-select"
                  value={selectedClientId}
                  onChange={handleClientChange}
                  placeholder="Select a client"
                >
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>

            {loading ? (
                <Spinner />
            ) : (
                <Table variant="simple">
                <Thead>
                    <Tr>
                    <Th>Configuration ID</Th>
                    <Th>Configuration Date</Th>
                    <Th isNumeric>Total Cases</Th>
                    <Th isNumeric>Mismatches</Th>
                    <Th isNumeric>Accuracy</Th>
                    <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {stats.map((row) => (
                        <Tr key={row.id}>
                            <Td>{row.id}</Td>
                            <Td>{new Date(row.created_at).toLocaleString()}</Td>
                            <Td isNumeric>{row.total_cases}</Td>
                            <Td isNumeric>{row.mismatches}</Td>
                            <Td isNumeric>{row.accuracy_percentage}%</Td>
                            <Td>
                                <Button size="sm" onClick={() => handleRowClick(row.id)}>View Details</Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
                </Table>
            )}
        </Box>
    </Box>
  );
};

export default ConfigurationAccuracyDashboard;